import { union } from 'lodash/array';
import { parsePhoneNumber } from 'awesome-phonenumber';
import queryString from 'query-string';
import moment from 'moment';
import 'moment/locale/nb';
import 'moment/locale/sv';
import 'moment/locale/da';
import * as H from 'history';
import { ProvidedService, LeapClaim, DebtorClaimEvent, Transaction } from '../api/client';

export const invoiceSystemPrefix = 'mmsys';
export const leapSystemPrefix = 'leap';

export const formatNationalIdentityNumber = (nationalIdentityNumber: string | undefined | null) => {
  if (nationalIdentityNumber === undefined || nationalIdentityNumber === null || nationalIdentityNumber.length <= 6) {
    return nationalIdentityNumber || '';
  }

  return nationalIdentityNumber.slice(0, 6) + ' ' + nationalIdentityNumber.slice(6);
};

const countryCallingCode: { key: string, value: number }[] = [
  { key: 'UnitedKingdom', value: 44 },
  { key: 'Denmark', value: 45 },
  { key: 'Sweden', value: 46 },
  { key: 'Norway', value: 47 },
  { key: 'Poland', value: 48 },
  { key: 'Germany', value: 49 },
  { key: 'FaroeIslands', value: 298 },
  { key: 'Iceland', value: 354 },
  { key: 'Finland', value: 358 }
];

export const validateCountryCode = (phoneNumber: string | undefined) => {
  if (phoneNumber === undefined || phoneNumber.length <= 6) {
    return false;
  }

  const regEx = new RegExp('^\\+?[0-9 ]+$'); // Only format phone numbers that may start with a plus sign and then only contains numbers or whitespaces.
  if (regEx.test(phoneNumber)) {
    let countryCode = parsePhoneNumber(phoneNumber, { regionCode: 'NO' }).countryCode;
    if (countryCallingCode.find(key => key.value === countryCode) !== undefined) {
      return true;
    }
  }

  return false;
}

export const formatEmailToDisplayName = (email: string): string => {
  const localPart = email.split('@')[0];
  const withSpaces = localPart.replace(/\./g, ' ');
  return toTitleCase(withSpaces);
};

export const formatPhoneNumber = (phoneNumber: string | undefined) => {
  if (phoneNumber === undefined || phoneNumber.length <= 6) {
    return phoneNumber || '';
  }

  const regEx = new RegExp('^\\+?[0-9 ]+$'); // Only format phone numbers that may start with a plus sign and then only contains numbers or whitespaces.
  if (!regEx.test(phoneNumber)) {
    return phoneNumber;
  }

  let countryCode = parsePhoneNumber(phoneNumber, { regionCode: 'NO' }).countryCode;
  if (countryCallingCode.find(key => key.value === countryCode) === undefined) {
    return '';
  }

  return parsePhoneNumber(phoneNumber, { regionCode: 'NO' }).number?.international;
};

export const formatDate = (date: Date | undefined, locale: string = 'nb') => {
  if (date === undefined) {
    return '';
  }

  return moment(date).locale(locale).format('L');
};

export const formatDateLong = (date: Date | undefined, locale: string = 'nb') => {
  if (date === undefined) {
    return '';
  }

  return moment(date).locale(locale).format('DD. MMMM YYYY HH:mm:ss');
};

export const formatTime = (date: Date | undefined, locale: string = 'nb') => {
  if (date === undefined) {
    return '';
  }

  return moment(date).locale(locale).format('LT');
};

export const formatEpochToTime = (milliseconds: number, locale: string = 'nb') => {
  return moment(milliseconds).locale(locale).format('LT');
};

export const createDebtorIdsQueryString = (debtorIds?: string[]) => {
  if (debtorIds === undefined) {
    return '';
  }

  return queryString.stringify({ debtorId: debtorIds.sort() });
};

export const getDebtorIdsFromQueryString = (location?: H.Location): string[] => {
  if (location === undefined) {
    return [];
  }

  const parsedQueryString = queryString.parse(location!.search);

  if (parsedQueryString.debtorId === undefined) {
    return [];
  } else if (typeof parsedQueryString.debtorId === 'string') {
    return [parsedQueryString.debtorId];
  } else {
    return parsedQueryString.debtorId?.filter((x): x is string => x !== null).sort() ?? [];
  }
};

export const getCaseNumbersFromQueryString = (location?: H.Location): string[] => {
  if (location === undefined) {
    return [];
  }

  const parsedQueryString = queryString.parse(location!.search);

  if (parsedQueryString.caseNumber === undefined) {
    return [];
  } else if (typeof parsedQueryString.caseNumber === 'string') {
    return [parsedQueryString.caseNumber];
  } else {
    return parsedQueryString.caseNumber?.filter((x): x is string => x !== null).sort() ?? [];
  }
}

export const getInvoiceSystemDebtorIds = (ids: string[]) => {
  return ids.filter(x => x.includes(invoiceSystemPrefix));
};

export const getLeapClaimSystemDebtorIds = (ids: string[]) => {
  return ids.filter(x => x.includes(leapSystemPrefix));
};

export const getStartingPageForLoginWithRoles = (roles: string[]) => {
  let redirectTo = '/';
  if (roles && roles.some(i => i === 'CustomerCareWorker' || i === 'Administrator')) {
    redirectTo = '/search';
  } else if (roles && roles.some(i => i === 'TerminalWorker')) {
    redirectTo = '/terminal';
  }
  console.log('getStartingPageForLoginWithRoles', roles, redirectTo);

  return redirectTo;
};

export const urlify = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return String(text).replace(urlRegex, '<a href="$1" target="_blank">$1</a>');
};

export const validateEmailAddress = (text: string) => {
  const regexp = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
  var valid = regexp.test(text);
  return valid;
};

export const getDescriptionForDisplay = (providedService: ProvidedService) => {
  var description = "Kravet gjelder tjenester mottatt";

  if (providedService.serviceProviderIsKnown) {
    description += " hos " + toTitleCase(providedService.serviceProvider!.trim());
  }

  if (moment(providedService.providedAt).year() > 2000) {
    description += " den " + formatDate(providedService.providedAt);
  }

  if (providedService.location) {
    description += " ved " + providedService.location.trim();
  }

  return description + ".";
}

const toTitleCase = (str: string | undefined) => (str ?? '')
  .toLowerCase()
  .split(' ')
  .map(word => (word.charAt(0).toUpperCase() + word.slice(1)).replace(/-./g, x => x.toUpperCase()))
  .join(' ');

export function updateEventsById(claims: LeapClaim[], eventReferenceId: string, newEvents: DebtorClaimEvent[], isDebtCollectionEvents: boolean) {
  return claims.map(claim => {
    
    if (claim.eventsReferenceId === eventReferenceId) {
        // Return a new object with updated events
      if (isDebtCollectionEvents) {
        return { ...claim, events: filterAndCombineDebtCollectionEvents(claim.events, newEvents).sort(sortEventsByDate), eventsHaveBeenFetched: true };
      }

      return { ...claim, events: union(claim.events, newEvents).sort(sortEventsByDate), eventsHaveBeenFetched: true };
    }

    if (claim.childClaims) {
        // Recursively update child claims
        const updatedChildren = updateEventsById(claim.childClaims, eventReferenceId, newEvents, isDebtCollectionEvents);
        return { ...claim, childClaims: updatedChildren };
    }

    // No match, return as is
    return claim;
  });
}

export const filterAndCombineDebtCollectionEvents = (existingEvents: DebtorClaimEvent[] | undefined, debtCollectionEvents: DebtorClaimEvent[]) => {
    if (!existingEvents) {
        return debtCollectionEvents;
    }

    var firstDebtCollectionDocumentDispatchedDate = existingEvents.filter(x => x.eventName === 'DebtCollectionDocumentDispatched').map(function (e) { return e.eventDate; }).sort()[0];
    if (firstDebtCollectionDocumentDispatchedDate) {

      // Moving cutoff one day back, in few cases the first document code was added to Predator the day before Leap shipped it
      var cutOffDate = moment(firstDebtCollectionDocumentDispatchedDate).subtract(1, "days").toDate();
      debtCollectionEvents = debtCollectionEvents.sort(sortEventsByDate);

      // return new events excluding events that are documents after cutoff date, as these are handled through Leap Claim Events
      var predatorEventsFiltered = debtCollectionEvents.filter(x => !x.isDocumentDispatched || moment(x.eventDate).toDate() <= cutOffDate);        
      var existingDocs = existingEvents.filter(x => x.isDocumentDispatched && moment(x.eventDate).toDate() >= cutOffDate);

      var updatedEvents = existingDocs.map(doc => {
        var event = debtCollectionEvents.find(dcDoc => doc.documentCode === dcDoc.documentCode && doc.eventDate && dcDoc.eventDate && doc.eventDate >= dcDoc.eventDate);
        if (event && event.documentDueDate) {
          doc.documentDueDate = event.documentDueDate;
          doc.description = event.description + " (Forfallsdato: " + formatDate(event.documentDueDate) + ")";
        }
        return doc;
      })

      return union(updatedEvents, predatorEventsFiltered);
    }

    return union(existingEvents, debtCollectionEvents);
    
}

export function updateTransactionsByKid(claims: LeapClaim[], kid: string, transactions: Transaction[]) {
  return claims.map(claim => {

    if (claim.kid === kid) {
      // Return a new object with updated events
      return { ...claim, transactionsOnKid: transactions, transactionsHaveBeenFetched: true };
    }

    if (claim.childClaims) {
      // Recursively update child claims
      const updatedChildren = updateTransactionsByKid(claim.childClaims, kid, transactions);
      return { ...claim, childClaims: updatedChildren };
    }

    // No match, return as is
    return claim;
  });
}
function sortEventsByDate<T extends DebtorClaimEvent>(a: T, b: T) {
  const dateA = a.eventDate ? +new Date(a.eventDate) : -Infinity;
  const dateB = b.eventDate ? +new Date(b.eventDate) : -Infinity;
  return dateB - dateA;
}